/**
 * useFirstThirtySeconds — instruments the critical 0-30s new-visitor
 * drop-off window per the Feb 2026 engagement audit (session avg fell
 * to 53s).
 *
 * ▸ Cycle 58 (Feb 2026) · Emergent Command · Event Schema Cleanup:
 *   Firing rules are now mutually-exclusive per session so activation
 *   funnel counts reconcile.
 *
 *   RULES (per session, strict):
 *     • `landed`                — fires ONCE per SESSION, immediately on
 *                                 mount when a session's landing view
 *                                 is first rendered.
 *     • `first_tap_10s`         — fires ONCE per session ONLY IF the
 *                                 first interaction lands ≤10s after
 *                                 `landed`. Also fires `first_tap_30s`
 *                                 (10s ⊂ 30s).
 *     • `first_tap_30s`         — fires ONCE per session ONLY IF the
 *                                 first interaction lands ≤30s.
 *     • `left_30s_no_interaction`
 *                               — fires ONCE per session ONLY IF the
 *                                 session ends within 30s with ZERO
 *                                 interaction. Mutually exclusive with
 *                                 first_tap_*.
 *     • `stayed_30s_no_tap`     — fires ONCE per session ONLY IF the
 *                                 30s mark passes without a tap AND
 *                                 the user hasn't left (positive marker).
 *
 *   Sanity guarantee: every session with `landed` resolves to EXACTLY
 *   one of {first_tap_10s, first_tap_30s only, left_30s_no_interaction,
 *   stayed_30s_no_tap}. No session can double-emit.
 *
 *   DebugView: append `?a11yq=debug` or set localStorage `mwq_ga_debug=1`
 *   to log a console warning if any of the invariants above are
 *   violated at runtime.
 */

import { useEffect } from "react";
import { trackEvent, isDebugMode, debugWarn } from "@/lib/analytics";

const SESSION_LANDED   = "we_first30s_landed_v1";
const SESSION_TAP_10   = "we_first30s_tap10_v1";
const SESSION_TAP_30   = "we_first30s_tap30_v1";
const SESSION_LEFT     = "we_first30s_left_v1";
const SESSION_STAYED   = "we_first30s_stayed_v1";

function _flag(key) {
  try { return sessionStorage.getItem(key) === "1"; } catch { return false; }
}
function _mark(key) {
  try { sessionStorage.setItem(key, "1"); } catch { /* ignore */ }
}

export default function useFirstThirtySeconds() {
  useEffect(() => {
    let disposed = false;

    // ─── landed — fire once per session ─────────────────────────────
    if (!_flag(SESSION_LANDED)) {
      _mark(SESSION_LANDED);
      trackEvent("landed", {
        at: Date.now(),
        ref: (document && document.referrer) || "",
      });
    } else if (isDebugMode()) {
      debugWarn("[funnel] landed already fired this session — hook remounted (expected in SPA nav).");
    }

    const startedAt = Date.now();
    let tapped = _flag(SESSION_TAP_30);

    const onTap = () => {
      if (disposed) return;
      // Mutual exclusion guard: if we've already resolved this
      // session (via bounce OR previous tap), do nothing.
      if (_flag(SESSION_TAP_30) || _flag(SESSION_LEFT)) {
        if (isDebugMode() && _flag(SESSION_LEFT)) {
          debugWarn("[funnel] pointerdown after left_30s_no_interaction fired — should be impossible.");
        }
        return;
      }
      tapped = true;
      const dt = Date.now() - startedAt;
      _mark(SESSION_TAP_30);
      trackEvent("first_tap_30s", { ms: dt });
      if (dt <= 10000) {
        _mark(SESSION_TAP_10);
        trackEvent("first_tap_10s", { ms: dt });
      }
      cleanup();
    };

    const bounce = (reason) => {
      if (disposed) return;
      if (tapped || _flag(SESSION_TAP_30)) return;
      if (_flag(SESSION_LEFT)) return;      // already fired
      const dt = Date.now() - startedAt;
      if (dt >= 30000) return;               // only in the first 30s
      _mark(SESSION_LEFT);
      trackEvent("left_30s_no_interaction", { ms: dt, reason });
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") bounce("hidden");
    };
    const onUnload = () => bounce("unload");
    const onPageHide = () => bounce("pagehide");

    document.addEventListener("pointerdown", onTap, { passive: true });
    document.addEventListener("keydown", onTap, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("beforeunload", onUnload);
    window.addEventListener("pagehide", onPageHide);

    // Positive marker if we cross 30s without a tap AND without bounce.
    const stayTimer = setTimeout(() => {
      if (disposed) return;
      if (tapped || _flag(SESSION_TAP_30) || _flag(SESSION_LEFT)) return;
      if (_flag(SESSION_STAYED)) return;
      _mark(SESSION_STAYED);
      trackEvent("stayed_30s_no_tap", {});
    }, 30_000);

    const cleanup = () => {
      disposed = true;
      clearTimeout(stayTimer);
      document.removeEventListener("pointerdown", onTap);
      document.removeEventListener("keydown", onTap);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("beforeunload", onUnload);
      window.removeEventListener("pagehide", onPageHide);
    };

    return cleanup;
  }, []);
}
